// src/utils/downloadUtils.ts

import { getDownloadLink as getOperationsDownloadLink } from '../services/toolsAPI/fileMergeSplitAPI';
import { getDownloadLink as getReducerDownloadLink } from '../services/toolsAPI/fileReducerAPI';

export type ToolType = 'reducer' | 'operations' | 'merge' | 'split' | 'unknown';

/**
 * Determines which tool a resultId belongs to based on patterns or explicit type parameter
 * @param resultId - The ID of the result to check
 * @returns - 'reducer', 'operations', or 'unknown'
 */
export const detectToolTypeFromId = (resultId: string | null | undefined): ToolType => {
    if (!resultId) return 'unknown';

    // Check if it's a demo ID with tool type embedded
    if (resultId.startsWith('demo-reducer-')) return 'reducer';
    if (resultId.startsWith('demo-merge-') || resultId.startsWith('demo-split-')) return 'operations';

    // Default to operations if we can't determine
    return 'operations';
};

/**
 * Gets the appropriate download link based on the tool type and resultId
 * @param resultId - The ID of the result to download
 * @param toolType - The type of tool
 * @returns - The download URL
 */
export const getToolDownloadLink = (resultId: string | null | undefined, toolType: ToolType): string => {
    if (!resultId) {
        console.error('DOWNLOAD UTIL ERROR - No resultId provided');
        return '#';
    }

    // For demo or mock resultIds
    if (resultId.startsWith('demo-') || resultId.startsWith('mock-')) {
        return URL.createObjectURL(new Blob(['Demo file content'], { type: 'application/pdf' }));
    }

    // Choose the appropriate download function based on the tool type
    if (toolType === 'reducer') {
        return getReducerDownloadLink(resultId);
    } else {
        // Default to operations (merge/split)
        return getOperationsDownloadLink(resultId);
    }
};

/**
 * Gets the download link for a file, determining the tool type based on URL or context
 * @param resultId - The ID of the result to download
 * @param toolType - Optional tool type override
 * @returns - The download URL
 */
export const getUnifiedDownloadLink = (resultId: string | null | undefined, toolType?: ToolType): string => {
    let effectiveToolType = toolType;

    // Check if we have a direct tool type specified
    if (!effectiveToolType) {
        // Try to detect tool type from the URL or resultId pattern
        const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

        if (currentPath.includes('/tools/reducer') || currentPath.includes('/tools/compress-pdf-images')) {
            effectiveToolType = 'reducer';
        } else if (
            currentPath.includes('/tools/merge') ||
            currentPath.includes('/tools/split') ||
            currentPath.includes('/tools/merge-split-pdf')
        ) {
            effectiveToolType = 'operations';
        } else {
            // Try to determine from resultId pattern
            effectiveToolType = detectToolTypeFromId(resultId);
        }
    }

    return getToolDownloadLink(resultId, effectiveToolType || 'unknown');
};
